import { ICompanyProfileResponse } from '../../../schemas/CompanyProfile.schema';
import { IAddCompanyToListRequest } from '../../../schemas/SourcingListItems.schema';
import {
  ICompanyProfileLookup,
  ISourcingTypeaheadResult,
} from '../../../services/queries/SourcingCompanyQueries';

/**
 * Pick the most specific lookup parameter for `fetchCompanyProfile` from a
 * typeahead row. The backend resolves CB UUID > HM URN/ID > PB id > domain
 * (mirrored from `resolvePrimaryKey`), so we prefer a provider/sourceId pair
 * when present and only fall back to `domain` if the row carries no provider
 * id. Returns `null` when no usable identifier is present — caller skips the
 * profile fetch in that case.
 */
export function buildProfileLookup(result: ISourcingTypeaheadResult): ICompanyProfileLookup | null {
  if (result.sourceId) {
    if (result.provider === 'cb') {
      return { cbUuid: result.sourceId };
    }
    if (result.provider === 'hm') {
      return { hmEntityUrn: result.sourceId };
    }
    if (result.provider === 'pb') {
      return { pbCompanyId: result.sourceId };
    }
  }
  if (result.domain) {
    return { domain: result.domain };
  }
  return null;
}

function trimOrUndefined(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function firstIndustry(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const s = trimOrUndefined(entry);
      if (s) {
        return s;
      }
    }
    return undefined;
  }
  const s = trimOrUndefined(value);
  if (!s) {
    return undefined;
  }
  // BE returns industry as a comma-separated string ("AI,Health Care") — take
  // the first segment so the grid cell shows a single readable label.
  return s.split(',')[0]?.trim() || undefined;
}

function headcountString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return trimOrUndefined(value);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

/**
 * Build the payload sent to `addCompaniesToList`. When a profile response is
 * available, populate the BE-supported attribute fields directly from the
 * flat profile shape (`profile.name`, `profile.industry`, ...). Each field
 * is forwarded only when present so the backend persists what we have and
 * leaves the rest to a manual Enrich Now.
 */
export function buildAddCompanyPayload(
  result: ISourcingTypeaheadResult,
  profile: ICompanyProfileResponse | null
): IAddCompanyToListRequest {
  const typeaheadName = trimOrUndefined(result.name);
  const typeaheadDomain = trimOrUndefined(result.domain);
  const typeaheadWebsite = trimOrUndefined(result.website);

  if (!profile) {
    return {
      ...(typeaheadName ? { name: typeaheadName } : {}),
      ...(typeaheadDomain ? { domain: typeaheadDomain } : {}),
      ...(typeaheadWebsite ? { website: typeaheadWebsite } : {}),
    };
  }

  const p = profile.profile;
  const profileName = trimOrUndefined(p.name);
  const profileDomain = trimOrUndefined(p.domain);
  const profileWebsite = trimOrUndefined(p.website);
  const industry = firstIndustry(p.industry);
  const sector = trimOrUndefined(p.sector) ?? trimOrUndefined(p.status?.universe);
  const linkedinUrl = trimOrUndefined(p.linkedinUrl);
  // Prefer the short description; fall back to the long one when only the
  // detailed copy is available so the grid cell isn't empty.
  const shortDescription = trimOrUndefined(p.shortDescription) ?? trimOrUndefined(p.longDescription);
  const logoUrl = trimOrUndefined(p.logoUrl);
  const headcount = headcountString(p.headcount);
  const foundedYear =
    typeof p.foundedYear === 'number' && Number.isFinite(p.foundedYear) ? p.foundedYear : undefined;

  // Typeahead picks beat profile picks for name/domain because the user just
  // clicked them — don't quietly rename the row they chose.
  const name = typeaheadName ?? profileName;
  const domain = typeaheadDomain ?? profileDomain;
  const website = profileWebsite ?? typeaheadWebsite;

  return {
    ...(name ? { name } : {}),
    ...(domain ? { domain } : {}),
    ...(website ? { website } : {}),
    ...(sector ? { sector } : {}),
    ...(industry ? { industry } : {}),
    ...(linkedinUrl ? { linkedinUrl } : {}),
    ...(shortDescription ? { shortDescription } : {}),
    ...(logoUrl ? { logoUrl } : {}),
    ...(headcount ? { headcount } : {}),
    ...(foundedYear != null ? { foundedYear } : {}),
  };
}
